/**
 * Workout Service - Supabase Integration
 * Handles all workout program and history CRUD operations
 */

import { supabase } from './supabaseClient';

/**
 * Get all public workout programs
 */
export const getPublicPrograms = async () => {
  try {
    const { data, error } = await supabase
      .from('workout_programs')
      .select('*')
      .eq('is_public', true)
      .order('created_at', { ascending: false });

    if (error) throw error;
    
    // Transform Supabase format to match what WorkoutLibrary expects
    const transformedData = (data || []).map(program => ({
      id: program.id,
      title: program.title,
      description: program.description,
      duration: program.duration || '4 weeks',
      level: program.level?.charAt(0).toUpperCase() + program.level?.slice(1) || 'Beginner',
      difficulty: program.level?.charAt(0).toUpperCase() + program.level?.slice(1) || 'Beginner',
      frequency: program.frequency || '',
      equipment: program.equipment || '',
      goals: Array.isArray(program.goals) ? program.goals : [],
      image: program.icon || '💪',
      icon: program.icon || '💪',
      icon_bg: program.icon_bg || '',
      weeks: program.weeks || [],
      totalWorkouts: program.total_workouts || 0,
      completedWorkouts: 0,
      progress: 0,
      status: program.status,
      updated_at: program.updated_at || program.created_at || null,
      progress_reset_at: program.progress_reset_at || null
    }));
    
    return { success: true, data: transformedData };
  } catch (error) {
    console.error('Error fetching programs:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Get a single program by ID
 */
export const getProgramById = async (id) => {
  try {
    const { data, error } = await supabase
      .from('workout_programs')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error fetching program:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Create a new workout program
 */
export const createProgram = async (programData) => {
  try {
    // Get current user's member ID
    const userEmail = localStorage.getItem('userEmail');
    let memberId = null;

    if (userEmail) {
      const { data: member } = await supabase
        .from('members')
        .select('id')
        .eq('email', userEmail)
        .single();
      
      memberId = member?.id;
    }

    const { data, error } = await supabase
      .from('workout_programs')
      .insert([{
        ...programData,
        created_by: memberId,
        is_public: true
      }])
      .select('*')
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error creating program:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Update an existing program
 */
export const updateProgram = async (id, programData) => {
  try {
    const updatePayload = { ...programData };

    // Only reset member progress when workouts are removed/replaced (a workout
    // ID that existed before is now gone). Text edits, exercise tweaks, added
    // workouts, and status changes all preserve member completions. The member
    // app filters on progress_reset_at — updated_at bumps on every save via a
    // DB trigger, so it can't be used for this.
    if (programData.weeks) {
      const { data: existing } = await supabase
        .from('workout_programs')
        .select('weeks')
        .eq('id', id)
        .single();

      const workoutIds = (weeks) =>
        new Set((weeks || []).flatMap(w => (w.workouts || []).map(wo => wo.id).filter(Boolean)));

      const oldIds = workoutIds(existing?.weeks);
      const newIds = workoutIds(programData.weeks);
      const workoutRemoved = [...oldIds].some(wid => !newIds.has(wid));

      if (workoutRemoved) {
        updatePayload.progress_reset_at = new Date().toISOString();
      }
    }

    let { data, error } = await supabase
      .from('workout_programs')
      .update(updatePayload)
      .eq('id', id)
      .select('*')
      .single();

    // If the progress_reset_at migration hasn't been run yet, retry without it
    if (error && error.message?.includes('progress_reset_at')) {
      delete updatePayload.progress_reset_at;
      const retry = await supabase
        .from('workout_programs')
        .update(updatePayload)
        .eq('id', id)
        .select('*')
        .single();
      data = retry.data;
      error = retry.error;
    }

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error updating program:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a program
 */
export const deleteProgram = async (id) => {
  try {
    // First delete related workout history
    await supabase
      .from('workout_history')
      .delete()
      .eq('program_id', id);
    
    // Then delete related program completions
    await supabase
      .from('program_completions')
      .delete()
      .eq('program_id', id);
    
    // Finally delete the program
    const { error } = await supabase
      .from('workout_programs')
      .delete()
      .eq('id', id);

    if (error) throw error;
    return { success: true };
  } catch (error) {
    console.error('Error deleting program:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Save workout history
 */
export const saveWorkoutHistory = async (memberEmail, workoutData) => {
  try {
    // Get member ID
    const { data: member, error: memberError } = await supabase
      .from('members')
      .select('id')
      .eq('email', memberEmail)
      .maybeSingle();

    if (memberError) {
      console.error('Error fetching member:', memberError);
      // Continue without member_id - workout history will still save
    }

    const { data, error } = await supabase
      .from('workout_history')
      .insert([{
        member_id: member?.id || null,
        user_email: memberEmail,
        completed_at: new Date().toISOString(),
        ...workoutData
      }])
      .select('*')
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error saving workout history:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get member's workout history
 */
export const getWorkoutHistory = async (memberEmail) => {
  try {
    // Try to get member ID
    let memberId = null;
    try {
      const { data: member } = await supabase
        .from('members')
        .select('id')
        .eq('email', memberEmail)
        .single();
      memberId = member?.id || null;
    } catch (e) { /* ignore */ }

    let data = [];
    if (memberId) {
      const { data: byId, error } = await supabase
        .from('workout_history')
        .select('*')
        .eq('member_id', memberId)
        .order('completed_at', { ascending: false });
      if (!error && byId) data = byId;
    }
    
    // Also check by user_email
    const { data: byEmail } = await supabase
      .from('workout_history')
      .select('*')
      .eq('user_email', memberEmail)
      .order('completed_at', { ascending: false });
    if (byEmail) {
      const existingIds = new Set(data.map(d => d.id));
      byEmail.forEach(w => { if (!existingIds.has(w.id)) data.push(w); });
    }

    return { success: true, data };
  } catch (error) {
    console.error('Error fetching workout history:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Save exercise progress (for PRs and tracking)
 */
export const saveExerciseProgress = async (memberEmail, progressData) => {
  try {
    // Try to get member ID (may not exist for all users)
    let memberId = null;
    try {
      const { data: member } = await supabase
        .from('members')
        .select('id')
        .eq('email', memberEmail)
        .single();
      memberId = member?.id || null;
    } catch (e) {
      console.log('Member lookup failed for exercise progress, saving with email only');
    }

    const insertData = {
      ...progressData
    };
    if (memberId) insertData.member_id = memberId;

    // Try with user_email first, fall back without it if column doesn't exist
    insertData.user_email = memberEmail;
    let { data, error } = await supabase
      .from('exercise_progress')
      .insert([insertData])
      .select('*')
      .single();

    // If user_email column doesn't exist, retry without it
    if (error && error.message?.includes('user_email')) {
      delete insertData.user_email;
      const retry = await supabase
        .from('exercise_progress')
        .insert([insertData])
        .select('*')
        .single();
      data = retry.data;
      error = retry.error;
    }

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error saving exercise progress:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get exercise progress for a specific exercise
 */
export const getExerciseProgress = async (memberEmail, exerciseId) => {
  try {
    // Get member ID
    const { data: member } = await supabase
      .from('members')
      .select('id')
      .eq('email', memberEmail)
      .single();

    if (!member) return { success: true, data: [] };

    const { data, error } = await supabase
      .from('exercise_progress')
      .select('*')
      .eq('member_id', member.id)
      .eq('exercise_id', exerciseId)
      .order('recorded_at', { ascending: false });

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching exercise progress:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Get all PRs for a member
 */
export const getMemberPRs = async (memberEmail) => {
  try {
    // Get member ID
    const { data: member } = await supabase
      .from('members')
      .select('id')
      .eq('email', memberEmail)
      .single();

    if (!member) return { success: true, data: [] };

    const { data, error } = await supabase
      .from('exercise_progress')
      .select('*')
      .eq('member_id', member.id)
      .eq('is_pr', true)
      .order('recorded_at', { ascending: false });

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching PRs:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Save program completion
 */
export const saveProgramCompletion = async (memberEmail, completionData) => {
  try {
    // Get member ID
    const { data: member, error: memberError } = await supabase
      .from('members')
      .select('id')
      .eq('email', memberEmail)
      .maybeSingle();

    if (memberError) {
      console.error('Error fetching member:', memberError);
      // Continue without member_id
    }

    const { data, error } = await supabase
      .from('program_completions')
      .insert([{
        member_id: member?.id || null,
        ...completionData
      }])
      .select('*')
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error) {
    console.error('Error saving program completion:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get program completions for a member
 */
export const getProgramCompletions = async (memberEmail, programId = null) => {
  try {
    // Get member ID
    const { data: member, error: memberError } = await supabase
      .from('members')
      .select('id')
      .eq('email', memberEmail)
      .single();

    if (!member) return { success: true, data: [] };

    let query = supabase
      .from('program_completions')
      .select('*')
      .eq('member_id', member.id);

    if (programId) {
      query = query.eq('program_id', programId);
    }

    const { data, error } = await query.order('completed_at', { ascending: false });

    if (error) throw error;
    return { success: true, data: data || [] };
  } catch (error) {
    console.error('Error fetching program completions:', error);
    return { success: false, error: error.message, data: [] };
  }
};

/**
 * Save completed workout with full details (for WorkoutTrackerV2)
 */
export const saveCompletedWorkout = async (memberEmail, workoutId, workoutData) => {
  try {
    // Try to get member ID (may not exist for all users)
    let memberId = null;
    try {
      const { data: member } = await supabase
        .from('members')
        .select('id')
        .eq('email', memberEmail)
        .single();
      memberId = member?.id || null;
    } catch (e) {
      console.log('Member lookup failed, saving with email only:', memberEmail);
    }

    const insertData = {
      workout_id: workoutId,
      user_email: memberEmail,
      workout_title: workoutData.workoutTitle || 'Workout',
      program_id: workoutData.programId || null,
      duration_seconds: workoutData.duration || 0,
      sets_completed: workoutData.setData || {},
      week_number: workoutData.weekNumber || null,
      is_completed: true,
      completed_at: new Date().toISOString(),
      notes: workoutData.notes || {},
      personal_bests: workoutData.personalBests || []
    };
    
    // Only include member_id if we found one
    if (memberId) insertData.member_id = memberId;

    const { data, error } = await supabase
      .from('workout_history')
      .insert([insertData])
      .select('*')
      .single();

    if (error) throw error;
    console.log('✅ Workout saved to Supabase:', data?.id);
    return { success: true, data };
  } catch (error) {
    console.error('Error saving completed workout:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get completed workouts for a member
 */
export const getCompletedWorkouts = async (memberEmail) => {
  try {
    console.log('🔍 getCompletedWorkouts called with email:', memberEmail);
    
    // Try to get member ID
    let memberId = null;
    try {
      const { data: member } = await supabase
        .from('members')
        .select('id')
        .eq('email', memberEmail)
        .single();
      memberId = member?.id || null;
      console.log('🔍 Found member_id:', memberId, 'for email:', memberEmail);
    } catch (e) {
      console.log('⚠️ Member lookup failed for:', memberEmail);
    }

    // Query by member_id OR user_email to catch all records
    let data = [];
    if (memberId) {
      // Try member_id first
      const { data: byId, error: err1 } = await supabase
        .from('workout_history')
        .select('*')
        .eq('member_id', memberId)
        .eq('is_completed', true)
        .order('completed_at', { ascending: false });
      console.log('🔍 Query by member_id returned:', byId?.length || 0, 'workouts');
      if (!err1 && byId) data = byId;
      
      // Also get any records saved by email (in case member_id wasn't set)
      const { data: byEmail } = await supabase
        .from('workout_history')
        .select('*')
        .eq('user_email', memberEmail)
        .eq('is_completed', true)
        .order('completed_at', { ascending: false });
      console.log('🔍 Query by email returned:', byEmail?.length || 0, 'workouts for:', memberEmail);
      if (byEmail) {
        // Merge, avoiding duplicates by id
        const existingIds = new Set(data.map(d => d.id));
        byEmail.forEach(w => { if (!existingIds.has(w.id)) data.push(w); });
      }
    } else {
      // No member ID — query by email only
      const { data: byEmail, error } = await supabase
        .from('workout_history')
        .select('*')
        .eq('user_email', memberEmail)
        .eq('is_completed', true)
        .order('completed_at', { ascending: false });
      console.log('🔍 Query by email (no member_id) returned:', byEmail?.length || 0, 'workouts for:', memberEmail);
      if (!error && byEmail) data = byEmail;
    }
    
    // Deduplicate records: remove duplicates based on workout_id + completed_at timestamp
    // This fixes any duplicate entries that may have been created in the database
    const uniqueData = [];
    const seen = new Set();
    (data || []).forEach(workout => {
      // Create unique key from workout_id and completion time (rounded to nearest second)
      const timestamp = workout.completed_at ? new Date(workout.completed_at).toISOString().split('.')[0] : '';
      const uniqueKey = `${workout.workout_id}_${timestamp}`;
      
      if (!seen.has(uniqueKey)) {
        seen.add(uniqueKey);
        uniqueData.push(workout);
      }
    });
    
    data = uniqueData;
    
    // Return both formats: keyed object for workout tracker AND raw array for counting
    const completedWorkouts = {};
    (data || []).forEach(workout => {
      const entry = {
        workoutId: workout.workout_id,
        completedAt: workout.completed_at,
        duration: workout.duration_seconds,
        progress: 100,
        setData: workout.sets_completed || {},
        notes: workout.notes || {},
        personalBests: workout.personal_bests || []
      };
      
      // Store with database row id for unique counting
      const uniqueKey = workout.id || workout.workout_id;
      completedWorkouts[uniqueKey] = entry;
      
      // ALSO store at workout_id so banner checks work
      // This ensures isCompleted = completedWorkouts[workout.id] finds it
      if (workout.workout_id) {
        completedWorkouts[workout.workout_id] = entry;
      }
    });
    
    return { success: true, data: completedWorkouts, totalCount: (data || []).length, rawData: data || [] };
  } catch (error) {
    console.error('Error fetching completed workouts:', error);
    return { success: false, error: error.message, data: {}, totalCount: 0, rawData: [] };
  }
};

/**
 * Get exercise history for a member (all exercises)
 */
export const getExerciseHistory = async (memberEmail) => {
  try {
    // Get member ID
    const { data: member } = await supabase
      .from('members')
      .select('id')
      .eq('email', memberEmail)
      .single();

    if (!member) return { success: true, data: {} };

    const { data, error } = await supabase
      .from('exercise_progress')
      .select('*')
      .eq('member_id', member.id)
      .order('recorded_at', { ascending: true });

    if (error) throw error;
    
    // Transform to match localStorage format, but group by exercise_name instead of exercise_id
    // This allows PR tracking to work across different workout versions
    const exerciseHistory = {};
    (data || []).forEach(record => {
      const exerciseName = record.exercise_name;
      if (!exerciseName) return; // Skip if no name
      
      if (!exerciseHistory[exerciseName]) {
        exerciseHistory[exerciseName] = [];
      }
      exerciseHistory[exerciseName].push({
        weight: record.weight,
        reps: record.reps,
        date: record.recorded_at,
        unit: record.weight_unit
      });
    });
    
    return { success: true, data: exerciseHistory };
  } catch (error) {
    console.error('Error fetching exercise history:', error);
    return { success: false, error: error.message, data: {} };
  }
};

export default {
  getPublicPrograms,
  getProgramById,
  createProgram,
  updateProgram,
  deleteProgram,
  saveWorkoutHistory,
  getWorkoutHistory,
  saveExerciseProgress,
  getExerciseProgress,
  getMemberPRs,
  saveProgramCompletion,
  getProgramCompletions,
  saveCompletedWorkout,
  getCompletedWorkouts,
  getExerciseHistory
};
